// Letter Ladder: change one letter at a time to make a new word. From the notes.

import { h, clear } from '@kit/dom';
import { pick, shuffle } from '@kit/rng';
import type { GameModule, GameContext } from '@shell/types';
import { makeKeyboard } from '@games/common/keyboard';
import { LADDER_STARTS, bandFor, chainGoal, missesBeforeHint } from '@content/words/ladder';
import dictJson from '@content/words/dict.json';
import validJson from '@content/words/valid.json';

/** common words: used for hints and "show me one", so suggestions are words she knows */
const COMMON = new Set<string>(dictJson as string[]);
/** every real word: used to accept what she types ("bile" is a word even if it is rare) */
const VALID = new Set<string>(validJson as string[]);
const ALPHA = 'abcdefghijklmnopqrstuvwxyz';

export function neighbors(word: string, used: Set<string>, pool: Set<string> = COMMON): string[] {
  const out: string[] = [];
  for (let i = 0; i < word.length; i++) {
    for (const ch of ALPHA) {
      if (ch === word[i]) continue;
      const w = word.slice(0, i) + ch + word.slice(i + 1);
      if (pool.has(w) && !used.has(w)) out.push(w);
    }
  }
  return out;
}

/** hints prefer common words, but fall back to any real word */
function hintNeighbors(word: string, used: Set<string>): string[] {
  const c = neighbors(word, used, COMMON);
  return c.length ? c : neighbors(word, used, VALID);
}

function mount(host: HTMLElement, ctx: GameContext) {
  const level = ctx.level;
  const band = bandFor(level);
  const goal = chainGoal(level);
  const used = new Set<string>();
  let word = pickStart();
  const chain: string[] = [word];
  used.add(word);
  let selected = -1;
  let misses = 0;
  let hintNext = false;
  let shows = 0;
  let correct = 0, attempts = 0;
  let longest = 1;
  const t0 = performance.now();
  let tProblem = performance.now();

  function pickStart(): string {
    const cands = shuffle(ctx.rng, LADDER_STARTS[band]).filter(w => !used.has(w) && neighbors(w, used).length >= 2);
    return cands[0] || pick(ctx.rng, LADDER_STARTS[band]);
  }

  const progress = h('div.row', { style: { justifyContent: 'space-between' } }, h('span.muted', null, 'Rung ', h('strong', null, '1'), ` of ${goal}`), h('button.btn.small.ghost', { onclick: () => ctx.speak(word) }, 'Say it'));
  const ladder = h('div.stack', { style: { gap: '4px', alignItems: 'center', minHeight: '60px' } });
  const tiles = h('div.row', { style: { justifyContent: 'center', gap: '8px', padding: '6px 0' } });
  const help = h('p.center.muted.small', null, 'Tap a letter, then pick a new one to make a new word.');
  const kb = makeKeyboard({ onKey: onLetter });
  const actions = h('div.grid2', null,
    h('button.btn.small', { onclick: showOne }, 'Show me one'),
    h('button.btn.small.ghost', { onclick: newWord }, 'New word'),
  );
  host.append(progress, ladder, tiles, help, kb.el, actions);

  function render() {
    clear(ladder);
    const shown = chain.slice(-4, -1);
    for (const w of shown) ladder.append(h('div.muted', { style: { fontWeight: '800', letterSpacing: '4px', textTransform: 'uppercase', opacity: '.6' } }, w));
    clear(tiles);
    [...word].forEach((ch, i) => {
      tiles.append(h('button.choice', {
        style: { minWidth: '52px', minHeight: '64px', fontSize: '30px', textTransform: 'uppercase', borderColor: selected === i ? 'var(--accent)' : '', boxShadow: selected === i ? '0 0 0 3px var(--accent) inset' : '' },
        onclick: () => { selected = i; render(); },
      }, ch));
    });
    (progress.querySelector('strong') as HTMLElement).textContent = String(chain.length);
    kb.setEnabled(selected >= 0);
  }

  function onLetter(ch: string) {
    if (selected < 0) { help.textContent = 'First tap the letter you want to change.'; return; }
    const next = word.slice(0, selected) + ch + word.slice(selected + 1);
    const ms = performance.now() - tProblem;
    if (next === word) { help.textContent = 'That is the same letter. Pick a different one.'; return; }
    attempts++;
    if (used.has(next)) {
      ctx.sound('buzz'); misses++;
      ctx.report({ skill: ctx.skill, level, correct: false, hintUsed: false, ms, problemId: `ladder:${word}` });
      help.textContent = `You already used "${next}". Try another.`;
      maybeHint();
      return;
    }
    if (!VALID.has(next)) {
      ctx.sound('buzz'); misses++;
      ctx.report({ skill: ctx.skill, level, correct: false, hintUsed: false, ms, problemId: `ladder:${word}` });
      help.textContent = `"${next}" is not a word I know. Try another letter.`;
      shakeTiles();
      maybeHint();
      return;
    }
    // valid
    ctx.sound('ding');
    correct++;
    ctx.report({ skill: ctx.skill, level, correct: true, hintUsed: hintNext, ms, problemId: `ladder:${word}` });
    hintNext = false; misses = 0;
    word = next; used.add(word); chain.push(word);
    longest = Math.max(longest, chain.length);
    selected = -1;
    tProblem = performance.now();
    help.textContent = COMMON.has(word) ? pick(ctx.rng, ['Nice!', 'That works!', 'Keep climbing!', 'Good one!']) : `"${word}" is a real word. Fancy!`;
    if (chain.length >= 7) ctx.charm('charm.ladder7');
    if (chain.length >= 10) ctx.charm('charm.ladder10');
    if (chain.length >= goal) { finish(); return; }
    if (neighbors(word, used, VALID).length === 0) {
      help.textContent = `"${word}" has no more steps. Here is a fresh word.`;
      setTimeout(newWord, 900);
    }
    render();
  }

  function shakeTiles() { tiles.classList.remove('shake'); void tiles.offsetWidth; tiles.classList.add('shake'); }

  function maybeHint() {
    if (misses >= missesBeforeHint(level)) {
      const ns = hintNeighbors(word, used);
      if (ns.length) {
        const target = pick(ctx.rng, ns);
        let pos = 0; for (let i = 0; i < word.length; i++) if (word[i] !== target[i]) pos = i;
        selected = pos; hintNext = true;
        help.textContent = `Hint: try changing the ${ordinal(pos + 1)} letter.`;
        render();
      }
    }
  }

  function showOne() {
    const ns = hintNeighbors(word, used);
    if (!ns.length) { newWord(); return; }
    if (shows >= 2) { help.textContent = 'You have used both of your shows. You can do this!'; return; }
    shows++;
    const w = pick(ctx.rng, ns);
    help.textContent = `One way: "${w}". Now it is your turn.`;
    word = w; used.add(w); chain.push(w); selected = -1; hintNext = true;
    if (chain.length >= goal) { finish(); return; }
    render();
  }

  function newWord() {
    word = pickStart(); used.add(word); chain.push(word); selected = -1; misses = 0;
    render();
  }

  function finish() {
    render();
    ctx.end({ attempts, correct, perfect: attempts === correct && shows === 0, durationMs: performance.now() - t0, notes: [`Longest ladder: ${longest} words`] });
  }

  render();
  return { destroy() { clear(host); } };
}

function ordinal(n: number): string { return n === 1 ? '1st' : n === 2 ? '2nd' : n === 3 ? '3rd' : `${n}th`; }

const mod: GameModule = {
  id: 'words.letter-ladder',
  title: 'Letter Ladder',
  blurb: 'Change one letter to make a new word. Climb as high as you can.',
  category: 'words',
  skills: ['phonics_spelling', 'vocabulary'],
  levelRange: [1, 10],
  unlock: 'always',
  mount,
};
export default mod;
